/**
 * Journey: Sperm Donation, Male
 *
 * Steps: Learn > Apply/Screen > Donate
 */

import type { JourneyDefinition, PlanStep } from "../plan.types";

const learnStep: PlanStep = {
  id: "learn",
  title: "Becoming a Sperm Donor",
  subtitle: "Help someone build their family",
  icon: "BookOpen",
  education: {
    context:
      "Sperm donation helps individuals and couples who need donor sperm to conceive. As a donor, you provide semen samples that are screened, processed, and stored for use by recipients.",
    details:
      "Requirements typically include:\n• Age 18-39 (varies by program)\n• Good general health\n• No significant genetic conditions or family history\n• Non-smoker, limited alcohol use\n• Willing to commit to regular donations over 6-12 months\n\nCompensation: $50-$150 per sample (varies by program)\nTime commitment: 1-2 visits per week for 6-12 months",
  },
  actions: [
    {
      label: "I understand this",
      variant: "primary",
      icon: "Check",
      signal: "complete-step",
    },
  ],
  completionSignal: { type: "manual" },
};

const screenStep: PlanStep = {
  id: "screening",
  title: "Donor Screening",
  subtitle: "Medical, genetic, and psychological evaluation",
  icon: "ClipboardCheck",
  education: {
    context:
      "Donor screening is thorough. Sperm banks need to ensure sample quality and screen for genetic and infectious conditions. The process takes 4-8 weeks and is free to you.",
  },
  actions: [],
  substeps: [
    {
      id: "application",
      title: "Complete application",
      education:
        "Initial questionnaire about health, family history, and background.",
      status: "not-started",
    },
    {
      id: "sa",
      title: "Initial semen analysis",
      education:
        "Evaluates count, motility, and morphology. Higher standards than general fertility: donors typically need >40M/mL count.",
      status: "not-started",
    },
    {
      id: "medical-exam",
      title: "Physical exam and blood work",
      education: "Comprehensive health screening including STI panel.",
      status: "not-started",
    },
    {
      id: "genetic-testing",
      title: "Genetic testing",
      education: "Expanded carrier screening for 100+ genetic conditions.",
      status: "not-started",
    },
    {
      id: "psychological-eval",
      title: "Psychological evaluation",
      education:
        "Discuss motivations, expectations, and implications of donation.",
      status: "not-started",
    },
    {
      id: "freeze-thaw-test",
      title: "Freeze-thaw test",
      education:
        "Tests how well your sperm survives the freezing process. Not all men pass this step: ~50% motility loss after thaw is normal, but donors need good post-thaw numbers.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "all-substeps" },
};

const donateStep: PlanStep = {
  id: "donate",
  title: "Active Donation",
  subtitle: "Regular sample deposits",
  icon: "Calendar",
  education: {
    context:
      "Once approved, you'll visit the sperm bank 1-2 times per week to provide samples. Abstain 2-5 days between visits for optimal quality. Most programs require a 6-12 month commitment.",
    details:
      "Guidelines during active donation:\n• Maintain consistent abstinence windows (2-5 days)\n• Avoid excessive heat exposure\n• Limit alcohol consumption\n• Notify the bank of any health changes or new medications\n• Quarterly STI re-screening is required",
  },
  actions: [
    { label: "Set your start date", variant: "primary", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

export const SPERM_DONATION: JourneyDefinition = {
  key: "sperm-donation",
  gender: "male",
  goal: "sperm-donation",
  pathway: "solo",
  title: "Sperm Donation",
  description: "Help someone build their family",
  steps: [learnStep, screenStep, donateStep],
};
